/**
 * AuditMovementSchedule — Phase 6a.12 per Spec §21.8.1.
 *
 * Shared utility consumed by Balance Sheet (Phase 6a.12) AND Movement
 * (Phase 6a.13) tabs. Pure function: given the engagement's `lastResult`
 * aggregations and an i18n `t`, returns a stable, canonical-row-ordered
 * structure ready to render.
 *
 * Why the canonical order matters: backend's `MovementRow[]` arrays may
 * be ordered arbitrarily (or omit rows when zero). For an audit working
 * paper, every IFRS movement category MUST appear in the same fixed
 * position on every engagement — even zero-valued rows. This utility
 * synthesizes `0` rows in canonical positions when the backend omits a
 * type so the rendered table never "shrinks" between engagements.
 *
 * Sign convention: backend amounts are already signed (negative for
 * outflows like payments and terminations). `isPositive` flags the
 * row's effective polarity so renderers can pick `+`/`-`/`±` prefixes.
 */

import type {
  EngagementAggregations,
  MovementRow as BackendMovementRow,
  MovementTable,
} from "@/types";

export type MovementRowType =
  | "ADDITIONS"
  | "TERMINATIONS"
  | "MODIFICATIONS"
  | "INTEREST_ACCRUAL"
  | "PAYMENTS"
  | "DEPRECIATION"
  | "DEPRECIATION_REVERSAL"
  | "INDEX_REMEASUREMENT"
  | "FX_REMEASUREMENT"
  | "FX_REVALUATION"
  | "RECLASSIFICATION";

export interface AuditMovementLine {
  /** Pre-resolved, audit-formatted label (already includes ± prefix). */
  label: string;
  /** Decimal string from backend; `"0"` if synthesized. */
  amount: string;
  type: MovementRowType;
  /** True when the amount is non-negative — drives renderer styling. */
  isPositive: boolean;
}

export interface AuditMovementGroup {
  opening: { label: string; amount: string };
  movements: AuditMovementLine[];
  closing: { label: string; amount: string };
}

export interface AuditMovementSchedule {
  rouAssetCost: AuditMovementGroup;
  rouAccumulatedDepreciation: AuditMovementGroup;
  leaseLiability: AuditMovementGroup;
  npv: {
    rouAssetNet: string;
    leaseLiabilityTotal: string;
    netPosition: string;
  };
  liabilitySplit: {
    closingShortTerm: string;
    closingLongTerm: string;
  };
}

/** Canonical IFRS audit-working-paper row order for each balance group. */
const CANONICAL_ROW_ORDER: Record<
  "rouAssetCost" | "rouAccumulatedDepreciation" | "leaseLiability",
  MovementRowType[]
> = {
  // SPEC-11 — ROU is non-monetary (IAS 21.23(b)); FX_REMEASUREMENT is a
  // liability-only category. Removing it keeps the BS-tab ROU cost card
  // free of permanently-zero rows. TYPE_TO_I18N_KEY / DEFAULT_POSITIVE_BY_TYPE
  // intentionally still include FX_REMEASUREMENT because the liability list
  // below renders it.
  rouAssetCost: [
    "ADDITIONS",
    "TERMINATIONS",
    "MODIFICATIONS",
    "INDEX_REMEASUREMENT",
  ],
  rouAccumulatedDepreciation: [
    "DEPRECIATION",
    "DEPRECIATION_REVERSAL",
    "MODIFICATIONS",
  ],
  leaseLiability: [
    // MINI_FIX_CF — ADDITIONS leads the liability list so the Balance Sheet
    // card foots opening + movements = closing. Backend already emits this
    // row in `leaseLiability.rows`; previously the canonical filter dropped
    // it on the BS surface (MovementTab has its own row list and was unaffected).
    "ADDITIONS",
    "INTEREST_ACCRUAL",
    "PAYMENTS",
    "MODIFICATIONS",
    "TERMINATIONS",
    "INDEX_REMEASUREMENT",
    "FX_REMEASUREMENT",
    "FX_REVALUATION",
    "RECLASSIFICATION",
  ],
};

/** i18n key per movement type (resolved by the injected `t` function). */
const TYPE_TO_I18N_KEY: Record<MovementRowType, string> = {
  ADDITIONS: "engagement.results.movement.types.additions",
  TERMINATIONS: "engagement.results.movement.types.terminations",
  MODIFICATIONS: "engagement.results.movement.types.modifications",
  INTEREST_ACCRUAL: "engagement.results.movement.types.interestAccrual",
  PAYMENTS: "engagement.results.movement.types.payments",
  DEPRECIATION: "engagement.results.movement.types.depreciation",
  DEPRECIATION_REVERSAL:
    "engagement.results.movement.types.depreciationReversal",
  INDEX_REMEASUREMENT:
    "engagement.results.movement.types.indexRemeasurement",
  FX_REMEASUREMENT: "engagement.results.movement.types.fxRemeasurement",
  FX_REVALUATION: "engagement.results.movement.types.fxRevaluation",
  RECLASSIFICATION: "engagement.results.movement.types.reclassification",
};

/**
 * Default "polarity" per movement type — used only to seed `isPositive`
 * on synthesized zero rows. When the backend provides a real row, the
 * sign is taken from the actual amount.
 */
const DEFAULT_POSITIVE_BY_TYPE: Record<MovementRowType, boolean> = {
  ADDITIONS: true,
  TERMINATIONS: false,
  MODIFICATIONS: true,
  INTEREST_ACCRUAL: true,
  PAYMENTS: false,
  DEPRECIATION: true,
  DEPRECIATION_REVERSAL: false,
  INDEX_REMEASUREMENT: true,
  FX_REMEASUREMENT: true,
  FX_REVALUATION: true,
  RECLASSIFICATION: false,
};

interface BuildScheduleParams {
  aggregations: EngagementAggregations;
  /** react-i18next `t` injected by the caller. */
  t: (key: string) => string;
}

export function buildAuditMovementSchedule({
  aggregations,
  t,
}: BuildScheduleParams): AuditMovementSchedule {
  const bs = aggregations.balanceSheet;
  const mv = aggregations.movement;

  const openingLabel = t("engagement.results.movement.opening");
  const closingLabel = t("engagement.results.movement.closing");

  const buildGroup = (
    table: MovementTable,
    canonical: MovementRowType[],
  ): AuditMovementGroup => {
    // Index backend rows by type. Last write wins if the backend ever
    // duplicates a type within a single group (defensive).
    const byType = new Map<MovementRowType, BackendMovementRow>();
    for (const row of table.rows) {
      byType.set(row.type as MovementRowType, row);
    }

    const movements: AuditMovementLine[] = canonical.map((type) => {
      const row = byType.get(type);
      if (row) {
        const amountNum = Number(row.amount);
        return {
          label: t(TYPE_TO_I18N_KEY[type]),
          amount: row.amount,
          type,
          isPositive: amountNum >= 0,
        };
      }
      // Synthesize a canonical zero row for missing types.
      return {
        label: t(TYPE_TO_I18N_KEY[type]),
        amount: "0",
        type,
        isPositive: DEFAULT_POSITIVE_BY_TYPE[type],
      };
    });

    return {
      opening: { label: openingLabel, amount: table.openingBalance },
      movements,
      closing: { label: closingLabel, amount: table.closingBalance },
    };
  };

  // Phase 6c.2 (Frontend Spec §21.8.2) — ROU surfaces are now
  // `AssetClassMovementTable`s (`{ byAssetClass, total }`); the Balance
  // Sheet tab continues to consume the legacy flat `AuditMovementGroup`
  // shape via this builder, so we read from `.total` for the
  // BS-driven Schedule. The Movement Tab bypasses this builder and
  // consumes `aggregations.movement.*` directly (per-class layout).
  return {
    rouAssetCost: buildGroup(
      mv.rouAssetCost.total,
      CANONICAL_ROW_ORDER.rouAssetCost,
    ),
    rouAccumulatedDepreciation: buildGroup(
      mv.rouAccumulatedDepreciation.total,
      CANONICAL_ROW_ORDER.rouAccumulatedDepreciation,
    ),
    leaseLiability: buildGroup(
      mv.leaseLiability,
      CANONICAL_ROW_ORDER.leaseLiability,
    ),
    npv: {
      rouAssetNet: bs.npv.rouAssetNet,
      leaseLiabilityTotal: bs.npv.leaseLiabilityTotal,
      netPosition: bs.npv.netPosition,
    },
    liabilitySplit: {
      closingShortTerm: bs.leaseLiability.closingShortTerm,
      closingLongTerm: bs.leaseLiability.closingLongTerm,
    },
  };
}
